import { isValidRefreshToken } from './authTokens';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { putBlobCoverUrl, getBlobCoverUrl } from '../utils/blobCoverCache';
import { validateThumbBlob } from '../utils/coverArt';
import {
  shouldBlockWindowsExclusivePlay,
  WINDOWS_EXCLUSIVE_KILL_SWITCH_ERROR,
} from '../utils/exclusiveKillSwitch';
import { bitPerfectBlockedMessage } from '../utils/connectorCapability';
import {
  isScanSkipsListCommandRejected,
  ScanSkipsListUnsupportedError,
} from '../utils/scanSetAsideList';
import {
  applyIpcFetchPatchIfNeeded,
  isIpcTransportBlockError,
  isIpcInvokeHangError,
  isMacOsDegradedIpcTransportHost,
  isTauriAclDenied,
  isCloudRelayTransportConfig,
  isLocalTransportSpineReady,
  logRelayLastResort,
  logTransportCommandFailed,
  logTransportPathUsed,
  markTransportRelayLastResortUsed,
  notifyTransportDegradedActive,
  notifyTransportLocalSpineHealthy,
  notifyTransportRelayHealed,
  resetTransportDegradedForTests,
  resetTransportBeaconCapForTests,
  type IpcTransportConfig,
  type IpcTransportProbeResult,
} from './ipcTransportDegraded';
// Only used when running inside the Tauri desktop app (window.__TAURI__ present)

export interface Renderer {
  id: string;
  name: string;
  location: string;
  control_url: string;
  volume_url: string;
  ip: string;
  model: string;
  manufacturer: string;
  transport_type?: string;
  raat_port?: number | null;
  is_roon_ready?: boolean;
  /** Set by connector when renderer is a verified PS Audio device (PMG, AirLens, etc.). */
  is_psaudio?: boolean;
  uses_stop_for_pause?: boolean;
  sink_protocols?: string[];
  can_play?: boolean;
  icon_url?: string;
  custom_name?: string | null;
  /** Per-device pause (ms) after SetAVTransportURI when sample rate changes. Connector v11.0.61+. */
  resync_delay_ms?: number;
  gapless_enabled?: boolean;
  avt_supports_set_next?: boolean;
  audio_device_id?: string;
  picker_selectable?: boolean;
  picker_disabled_reason?: string | null; fixed_volume?: boolean; volume_not_controllable?: boolean;
}

/** Allowed resync delay presets — must match Connector `RESYNC_DELAY_PRESETS_MS`. */
export const RESYNC_DELAY_PRESETS_MS = [0, 100, 250, 500, 1000] as const;

export interface SetAvTransportUriOutcome {
  status: 'ok' | 'timeout' | 'soap_error' | string;
  detail?: string | null;
}

export interface PlayLocalFileResult {
  success: boolean;
  proxyUrl: string;
  transport: string;
  setAvTransportUri?: SetAvTransportUriOutcome | null;
  streamHost?: string | null;
  error?: string | null;
  /** Network DSD ladder delivery label (#1138), e.g. "DSD64 → PCM 176.4 kHz". */
  networkFormatLabel?: string | null;
}

/** Map renderer transport_type to support-log label. */
export function networkRendererTransportLabel(transportType?: string | null): string {
  switch (transportType) {
    case 'bluesound':
      return 'Bluesound';
    case 'raat':
      return 'RAAT';
    default:
      return transportType === 'qobuz-connect' ? 'Qobuz Connect' : 'UPnP/AVTransport';
  }
}

export function isConnectorAvailable(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

// Eager HTTPS fetch patch — macOS 26 only (#272, #275). Poll until __TAURI__ exists.
let _eagerIpcFetchPatchTimer: ReturnType<typeof setInterval> | null = null;

export function cancelEagerIpcFetchPatchForTests(): void {
  if (_eagerIpcFetchPatchTimer != null) {
    clearInterval(_eagerIpcFetchPatchTimer);
    _eagerIpcFetchPatchTimer = null;
  }
}

function scheduleEagerIpcFetchPatch(): void {
  if (typeof window === 'undefined') return;
  if (!isMacOsDegradedIpcTransportHost()) return;
  const attempt = (): boolean => {
    if (typeof window === 'undefined') return true;
    if (window.location?.protocol === 'https:' && isConnectorAvailable()) {
      applyIpcFetchPatchIfNeeded(false);
      return true;
    }
    return false;
  };
  if (attempt()) return;
  let tries = 0;
  _eagerIpcFetchPatchTimer = setInterval(() => {
    if (typeof window === 'undefined') {
      cancelEagerIpcFetchPatchForTests();
      return;
    }
    tries += 1;
    if (attempt() || tries >= 500) {
      cancelEagerIpcFetchPatchForTests();
    }
  }, 10);
}
scheduleEagerIpcFetchPatch();

/** True when Tauri IPC custom protocol failed and postMessage fallback is active (slower). */
let _ipcPostMessageFallback = false;

/** Hard ceiling for ensure_fresh_token — hung invokes must never block refresh dedupe forever. */
export const ENSURE_FRESH_TOKEN_TIMEOUT_MS = 10_000;

const IPC_TRANSPORT_PROBE_MS = 2_000;
const IPC_TRANSPORT_MAX_WAIT_MS = 15_000;
/** Degraded postMessage invokes need headroom — Rust may still be processing. */
const IPC_DEGRADED_INVOKE_TIMEOUT_MS = 45_000;
/** #10426-a1: exclusive_play only — Connector ≤ v11.0.327 bounds its engine open at 8s, ≥ v11.0.328 returns in ~8s. */
export const EXCLUSIVE_PLAY_INVOKE_TIMEOUT_MS = 10_000;

/** Non-transport invoke timeout. exclusive_play is capped in BOTH IPC modes; every other command keeps its cap. */
export function nonTransportInvokeTimeoutMs(command: string, degraded: boolean): number {
  if (command === 'exclusive_play') return EXCLUSIVE_PLAY_INVOKE_TIMEOUT_MS;
  return degraded ? IPC_DEGRADED_INVOKE_TIMEOUT_MS : 0;
}

let _ipcTransportReadyPromise: Promise<void> | null = null;
let _ipcForcePostMessage = false;
/** macOS: tier-1 loopback fetch known-blocked for this session (#414 B). */
let _macOsTier1LoopbackBlocked: boolean | null = null;
/** Feature-detect tier-2 URI scheme — null = not yet probed (#414 B / #351 B2). */
let _uriSchemeTransportSupported: boolean | null = null;
/** Feature-detect tier-3 postMessage bridge — null = not yet probed (#414 B / #351 B2). */
let _localTransportBridgeSupported: boolean | null = null;

/** Rust command Err — invoke channel delivered; do not relay (#414 A). */
export class TransportCommandError extends Error {
  readonly tier: number;
  readonly command: string;

  constructor(tier: number, command: string, message: string) {
    super(message);
    this.name = 'TransportCommandError';
    this.tier = tier;
    this.command = command;
  }
}

/** Background polling must never reach tier-5 relay (#445). */
export class LocalTiersExhaustedError extends Error {
  readonly command: string;

  constructor(command: string) {
    super(`local tiers exhausted: ${command}`);
    this.name = 'LocalTiersExhaustedError';
    this.command = command;
  }
}

export function isLocalTiersExhaustedError(err: unknown): err is LocalTiersExhaustedError {
  return err instanceof LocalTiersExhaustedError;
}

/** True when a Tauri invoke error means the channel failed, not a Rust command Err (#414 A). */
export function isTransportInvokePathFailure(err: unknown): boolean {
  if (isTauriAclDenied(err)) return true;
  if (isIpcTransportBlockError(err)) return true;
  if (isIpcInvokeHangError(err)) return true;
  const msg = String(err).toLowerCase();
  return msg.includes('ipc invoke timeout')
    || msg.includes('unknown command')
    || msg.includes('not found')
    || msg.includes('local_transport_bridge');
}

function isUnknownLocalTransportBridgeError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('unknown command')
    || msg.includes('not found')
    || (msg.includes('local_transport_bridge') && !msg.includes('volume'));
}

function isUriSchemeTransportUnavailableError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('failed to fetch')
    || msg.includes('err_unknown_url_scheme')
    || msg.includes('unknown url scheme')
    || msg.includes('url scheme')
    || msg.includes('not supported')
    || msg.includes('load failed')
    || msg.includes('network error');
}

function isLocalTransportSpineNotReadyError(err: unknown): boolean {
  return String(err).includes('local transport spine not ready');
}

function isFetchPathFailure(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  const msg = String(err).toLowerCase();
  return msg.includes('failed to fetch')
    || msg.includes('network error')
    || msg.includes('aborted')
    || msg.includes('load failed')
    || msg.includes('local http transport failed')
    || msg.includes('uri scheme transport unavailable')
    || msg.includes('local transport bridge unsupported');
}

/** True when a tier failure is a delivery-path problem, not a Rust command Err (#414 A). */
export function isTransportPathFailure(err: unknown): boolean {
  if (err instanceof TransportCommandError) return false;
  if (isLocalTransportSpineNotReadyError(err)) return true;
  if (isTransportInvokePathFailure(err)) return true;
  if (isFetchPathFailure(err)) return true;
  if (isUriSchemeTransportUnavailableError(err)) return true;
  return false;
}

function invokeErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  if (typeof err === 'string' && err.trim()) return err.trim();
  if (err != null && typeof err === 'object') {
    const record = err as Record<string, unknown>;
    if (typeof record.message === 'string' && record.message.trim()) return record.message.trim();
    if (typeof record.error === 'string' && record.error.trim()) return record.error.trim();
  }
  return String(err);
}

function markMacOsTier1LoopbackBlocked(reason?: string): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (_macOsTier1LoopbackBlocked) return;
  _macOsTier1LoopbackBlocked = true;
  if (reason) {
    diagnosticLog('info', 'transport', 'macos_tier1_loopback_skipped', { reason });
  }
}

function shouldSkipMacOsTier1Loopback(cfg: IpcTransportConfig | null): boolean {
  if (!isMacOsDegradedIpcTransportHost()) return false;
  if (_macOsTier1LoopbackBlocked === true) return true;
  if (_ipcForcePostMessage || _ipcPostMessageFallback) {
    markMacOsTier1LoopbackBlocked('force_post_message');
    return true;
  }
  if (cfg?.force_post_message) {
    markMacOsTier1LoopbackBlocked('host_force_post_message');
    return true;
  }
  return false;
}

export function isIpcPostMessageFallbackActive(): boolean {
  return _ipcPostMessageFallback;
}

function markIpcPostMessageFallback(reason = 'probe'): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (!_ipcPostMessageFallback) {
    _ipcPostMessageFallback = true;
    applyIpcFetchPatchIfNeeded(true);
    console.warn('[connector] IPC custom protocol unavailable — postMessage fallback active (probe backoff doubled)');
    console.info('[connector] transport_degraded=postMessage');
    diagnosticLog('warn', 'protocol', 'transport_degraded=postMessage', {
      transport_degraded: 'postMessage',
      reason,
    });
  }
}

/** User-visible banner — local spine healthy, cloud fallback note, or broken warning (#248 B4). */
function resolveTransportBanner(
  cfg: IpcTransportConfig | null,
  probeOk: boolean,
  reason: string,
): void {
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (probeOk && isLocalTransportSpineReady(cfg)) {
    notifyTransportLocalSpineHealthy({ reason });
    return;
  }
  if (probeOk && isCloudRelayTransportConfig(cfg)) {
    notifyTransportRelayHealed({ reason });
    return;
  }
  if (!probeOk) {
    notifyTransportDegradedActive(reason, {
      transport_degraded: isCloudRelayTransportConfig(cfg) ? 'cloud_relay' : 'postMessage',
    });
  }
}

let _ipcTransportConfigCache: IpcTransportConfig | null | undefined;

async function loadIpcTransportConfig(): Promise<IpcTransportConfig | null> {
  if (_ipcTransportConfigCache !== undefined) return _ipcTransportConfigCache;
  if (!isConnectorAvailable()) {
    _ipcTransportConfigCache = null;
    return null;
  }
  try {
    const cfg = await Promise.race([
      rawTauriInvoke<IpcTransportConfig>('get_ipc_transport_config'),
      new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error('ipc transport config timeout')),
          IPC_TRANSPORT_PROBE_MS,
        );
      }),
    ]);
    _ipcTransportConfigCache = cfg;
    return cfg;
  } catch (err) {
    if (isTauriAclDenied(err)) throw err;
    _ipcTransportConfigCache = null;
    return null;
  }
}

/** Round-trip probe for degraded-mode verification (#272). */
export async function probeIpcTransportRoundTrip(probeId?: string): Promise<IpcTransportProbeResult> {
  return rawTauriInvoke<IpcTransportProbeResult>('ipc_transport_probe', {
    probeId: probeId ?? `web-${Date.now()}`,
  });
}

/** Returns `legacy` when config IPC is absent/hung; `configured` when host forces postMessage. macOS only (#275). */
async function ensureIpcFetchPatchFromHost(): Promise<'legacy' | 'configured' | 'none'> {
  if (!isMacOsDegradedIpcTransportHost()) return 'none';
  const cfg = await loadIpcTransportConfig();
  if (cfg?.force_post_message) {
    _ipcForcePostMessage = true;
    applyIpcFetchPatchIfNeeded(true);
    let probeOk = false;
    try {
      await Promise.race([
        probeIpcTransportRoundTrip('host-config-verify'),
        new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error('ipc transport probe timeout')),
            IPC_TRANSPORT_PROBE_MS,
          );
        }),
      ]);
      probeOk = true;
    } catch (err) {
      if (isTauriAclDenied(err)) throw err;
      markIpcPostMessageFallback('macos_26_host_config_probe_failed');
      resolveTransportBanner(cfg, false, 'macos_26_host_config_probe_failed');
      return 'configured';
    }
    markIpcPostMessageFallback('macos_26_host_config');
    resolveTransportBanner(cfg, probeOk, 'macos_26_host_config');
    return 'configured';
  }
  if (cfg === null && typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    applyIpcFetchPatchIfNeeded(true);
    markIpcPostMessageFallback('legacy_hang_probe');
    resolveTransportBanner(null, false, 'legacy_hang_probe');
    return 'legacy';
  }
  return 'none';
}

/**
 * Wait until Tauri IPC can round-trip a lightweight command. Boot auth invokes
 * must not fire into the dying custom-protocol channel before postMessage fallback.
 */
export async function waitForIpcTransportReady(): Promise<void> {
  if (!isConnectorAvailable()) return;
  if (!isMacOsDegradedIpcTransportHost()) return;
  if (_ipcTransportReadyPromise) return _ipcTransportReadyPromise;
  _ipcTransportReadyPromise = (async () => {
    const hostResult = await ensureIpcFetchPatchFromHost();
    if (hostResult === 'legacy' || hostResult === 'configured') {
      return;
    }
    const deadline = Date.now() + IPC_TRANSPORT_MAX_WAIT_MS;
    let attempt = 0;
    while (Date.now() < deadline) {
      attempt += 1;
      try {
        await Promise.race([
          probeIpcTransportRoundTrip(`ready-${attempt}`),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('ipc transport probe timeout')), IPC_TRANSPORT_PROBE_MS);
          }),
        ]);
        if (_ipcPostMessageFallback) {
          console.info('[connector] IPC transport ready (postMessage fallback active)');
        }
        resolveTransportBanner(await loadIpcTransportConfig(), true, 'ready_probe_ok');
        return;
      } catch (err) {
        if (isTauriAclDenied(err)) {
          console.error('[connector] IPC transport probe ACL denied — check capabilities/default.json', err);
          throw err;
        }
        if (isIpcTransportBlockError(err) || isIpcInvokeHangError(err)) {
          markIpcPostMessageFallback(String(err));
          await new Promise((r) => setTimeout(r, 300 * Math.min(attempt, 4)));
          continue;
        }
        // Transport responded (even with a command error) — channel is alive.
        return;
      }
    }
    console.warn('[connector] IPC transport ready gate timed out — proceeding with bounded invoke retries');
    markIpcPostMessageFallback('ready_gate_timeout');
    resolveTransportBanner(await loadIpcTransportConfig(), false, 'ready_gate_timeout');
  })();
  return _ipcTransportReadyPromise;
}

// Start transport-ready gate at module load — macOS degraded hosts only (#275).
if (isConnectorAvailable() && isMacOsDegradedIpcTransportHost()) {
  void waitForIpcTransportReady();
}

/** Reset transport gate (tests only). */
export function resetIpcTransportGateForTests(): void {
  cancelEagerIpcFetchPatchForTests();
  _ipcTransportReadyPromise = null;
  _ipcPostMessageFallback = false;
  _ipcForcePostMessage = false;
  _macOsTier1LoopbackBlocked = null;
  _uriSchemeTransportSupported = null;
  _localTransportBridgeSupported = null;
  _ipcTransportConfigCache = undefined;
  resetTransportDegradedForTests();
  resetTransportBeaconCapForTests();
  if (typeof window !== 'undefined') {
    delete (window as any).__MAESTRO_IPC_FORCE_POSTMESSAGE__;
  }
}

/** Seed cached transport config (tests only). */
export function seedIpcTransportConfigForTests(cfg: IpcTransportConfig | null): void {
  _ipcTransportConfigCache = cfg;
}

function loopbackFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch(input, init);
  }
  return fetch(input, init);
}

const TRANSPORT_COMMANDS = new Set([
  'play',
  'pause',
  'stop',
  'seek',
  'get_volume',
  'set_volume',
  'get_transport_info',
  'play_uri',
  'set_next_uri',
  'clear_next_uri',
  'play_local_file',
  'multizone_play',
  'multizone_pause',
  'multizone_stop',
]);

const CLOUD_RELAY_TRANSPORT_URL = 'https://api.maestroaudio.net/api/transport/relay';

function resolveTransportCommandName(
  command: string,
  args?: Record<string, unknown>,
): string {
  if (command === 'volume') {
    if (args && (args.volume !== undefined || args.level !== undefined)) {
      return 'set_volume';
    }
    return 'get_volume';
  }
  if (command === 'resume') return 'play';
  return command;
}

export function isTransportCommand(command: string, args?: Record<string, unknown>): boolean {
  return TRANSPORT_COMMANDS.has(resolveTransportCommandName(command, args));
}

function localTransportPort(cfg: IpcTransportConfig): number {
  return cfg.port ?? cfg.proxy_port ?? 0;
}

function unwrapLocalTransportPayload(payload: unknown): unknown {
  if (payload != null && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (record.ok === false) {
      const err = record.error;
      throw new Error(typeof err === 'string' ? err : 'local HTTP transport failed');
    }
    if ('result' in record) return record.result;
  }
  return payload;
}

function relayTransportTimeoutMs(command: string): number {
  return (command === 'play_uri' || command === 'play_local_file') ? 15_000 : 8_000;
}

/** POST tier-1 local transport invoke — loopback only (#248 B1). */
export async function postLocalTransportInvoke(
  command: string,
  args?: Record<string, unknown>,
): Promise<unknown> {
  const resolved = resolveTransportCommandName(command, args);
  const cfg = await loadIpcTransportConfig();
  if (!isLocalTransportSpineReady(cfg)) {
    throw new Error('local transport spine not ready');
  }
  const port = localTransportPort(cfg!);
  const token = cfg!.token!.trim();
  const url = `http://127.0.0.1:${port}/api/transport/invoke`;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), relayTransportTimeoutMs(resolved))
    : undefined;
  try {
    const resp = await loopbackFetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ command: resolved, args: args ?? {} }),
      signal: controller?.signal,
    });
    const body = (await resp.json().catch(() => ({}))) as { ok?: boolean; error?: string; result?: unknown };
    if (!resp.ok) {
      const errText = body.error?.trim();
      if (errText) {
        throw new TransportCommandError(1, resolved, errText);
      }
      throw new Error(body.error?.trim() || `local HTTP transport failed (${resp.status}): ${resolved}`);
    }
    if (body.ok === false) {
      throw new TransportCommandError(
        1,
        resolved,
        body.error?.trim() || `local HTTP daemon error: ${resolved}`,
      );
    }
    logTransportPathUsed(1, resolved);
    if (isMacOsDegradedIpcTransportHost()) {
      notifyTransportLocalSpineHealthy({ command: resolved });
    }
    return unwrapLocalTransportPayload(body);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Tier-2 custom URI scheme when loopback fetch is blocked (#414 B / #277). */
export async function postUriSchemeTransportInvoke(
  command: string,
  args?: Record<string, unknown>,
): Promise<unknown> {
  if (_uriSchemeTransportSupported === false) {
    throw new Error('uri scheme transport unavailable');
  }
  const resolved = resolveTransportCommandName(command, args);
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), relayTransportTimeoutMs(resolved))
    : undefined;
  try {
    const resp = await loopbackFetch('maestro-transport://invoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command: resolved, args: args ?? {} }),
      signal: controller?.signal,
    });
    const body = (await resp.json().catch(() => ({}))) as { ok?: boolean; error?: string; result?: unknown };
    if (!resp.ok) {
      const errText = body.error?.trim();
      if (errText) {
        throw new TransportCommandError(2, resolved, errText);
      }
      throw new Error(body.error?.trim() || `URI scheme transport failed (${resp.status}): ${resolved}`);
    }
    if (body.ok === false) {
      throw new TransportCommandError(
        2,
        resolved,
        body.error?.trim() || `URI scheme daemon error: ${resolved}`,
      );
    }
    _uriSchemeTransportSupported = true;
    logTransportPathUsed(2, resolved);
    return unwrapLocalTransportPayload(body);
  } catch (err) {
    if (isUriSchemeTransportUnavailableError(err)) {
      _uriSchemeTransportSupported = false;
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Tier-3 postMessage bridge when URI scheme is absent (#414 B / #277). */
export async function postLocalTransportBridgeInvoke(
  command: string,
  args?: Record<string, unknown>,
): Promise<unknown> {
  if (_localTransportBridgeSupported === false) {
    throw new Error('local transport bridge unsupported');
  }
  const resolved = resolveTransportCommandName(command, args);
  try {
    const result = await rawTauriInvoke<unknown>('local_transport_bridge', {
      command: resolved,
      args: args ?? {},
    });
    _localTransportBridgeSupported = true;
    logTransportPathUsed(3, resolved);
    return result;
  } catch (err) {
    if (isUnknownLocalTransportBridgeError(err)) {
      _localTransportBridgeSupported = false;
      throw err;
    }
    if (isTransportInvokePathFailure(err)) {
      throw err;
    }
    throw new TransportCommandError(3, resolved, invokeErrorMessage(err));
  }
}

async function postCloudRelayTransport<T>(
  command: string,
  args: Record<string, unknown> | undefined,
  cfg: IpcTransportConfig | null,
): Promise<T> {
  const resolved = resolveTransportCommandName(command, args);
  const relayToken = cfg?.relay_token?.trim();
  const machineId = cfg?.machine_id?.trim();
  if (!relayToken || !machineId) {
    throw new Error('cloud relay not ready — relay token optional for local play');
  }
  logRelayLastResort('local_tiers_exhausted', resolved);
  markTransportRelayLastResortUsed();
  notifyTransportRelayHealed({ command: resolved, reason: 'relay_last_resort' });
  const correlationId = `relay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), relayTransportTimeoutMs(resolved))
    : undefined;
  try {
    const resp = await loopbackFetch(CLOUD_RELAY_TRANSPORT_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${relayToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        machine_id: machineId,
        correlation_id: correlationId,
        command: resolved,
        args: args ?? {},
        ts: Date.now(),
      }),
      signal: controller?.signal,
    });
    const body = (await resp.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string | { message?: string; code?: string };
      result?: unknown;
    };
    if (!resp.ok) {
      const errObj = body.error;
      const errMsg = typeof errObj === 'string'
        ? errObj
        : (errObj?.message || errObj?.code || `cloud relay failed: ${resolved}`);
      throw new Error(typeof errMsg === 'string' ? errMsg : String(errMsg));
    }
    if (body.ok === false) {
      const errObj = body.error;
      const errMsg = typeof errObj === 'string'
        ? errObj
        : (errObj?.message || String(errObj) || `cloud relay daemon error: ${resolved}`);
      throw new Error(errMsg);
    }
    logTransportPathUsed(5, resolved);
    return unwrapLocalTransportPayload(body) as T;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function invokeTauriWithRetries<T>(
  command: string,
  args: Record<string, unknown> | undefined,
  timeoutMs: number,
): Promise<T> {
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      if (timeoutMs > 0) {
        return await invokeIpcWithTimeout<T>(command, args, timeoutMs);
      }
      return await rawTauriInvoke<T>(command, args);
    } catch (err) {
      if (isTauriAclDenied(err)) throw err;
      const isIpcBlock = isIpcTransportBlockError(err) || isIpcInvokeHangError(err);
      // #10426-a1: never re-send exclusive_play after its IPC timeout — each resend re-opens the DAC
      // behind the shared fallback. The caller falls back to shared instead.
      const nonIdempotentTimeout = command === 'exclusive_play' && /ipc invoke timeout/i.test(String(err));
      if (isIpcBlock && attempt < 3 && !nonIdempotentTimeout) {
        markIpcPostMessageFallback(String(err));
        if (command === 'play_local_file' && attempt > 0) {
          diagnosticLog('info', 'playback', 'play_local_file IPC retry', {
            attempt: attempt + 1,
          });
        }
        await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
      } else {
        throw err;
      }
    }
  }
  throw new Error(`invoke('${command}') failed after retries`);
}

const TRANSPORT_TIER_EXHAUSTED = Symbol('transport_tier_exhausted');

type TransportLadderOptions = {
  /** Background reconcile/volume hydration — tiers 1–4 only, never relay (#445). */
  localOnly?: boolean;
};

async function runTransportLadder<T>(
  command: string,
  args: Record<string, unknown> | undefined,
  timeoutMs: number,
  options?: TransportLadderOptions,
): Promise<T> {
  const resolved = resolveTransportCommandName(command, args);
  const cfg = await loadIpcTransportConfig();

  const attemptTier = async (tier: number, fn: () => Promise<T>): Promise<T | typeof TRANSPORT_TIER_EXHAUSTED> => {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof TransportCommandError) {
        logTransportCommandFailed(err.tier, err.command, err.message);
        throw err;
      }
      if (tier === 1 && isMacOsDegradedIpcTransportHost() && isTransportPathFailure(err)) {
        markMacOsTier1LoopbackBlocked(String(err));
      }
      if (isTransportPathFailure(err)) {
        diagnosticLog('info', 'transport', `transport_tier${tier}_path_failure`, {
          command: resolved,
          tier,
          error: invokeErrorMessage(err),
        });
        return TRANSPORT_TIER_EXHAUSTED;
      }
      diagnosticLog('info', 'transport', `transport_tier${tier}_path_failure`, {
        command: resolved,
        tier,
        error: invokeErrorMessage(err),
      });
      return TRANSPORT_TIER_EXHAUSTED;
    }
  };

  if (isLocalTransportSpineReady(cfg) && !shouldSkipMacOsTier1Loopback(cfg)) {
    const tier1 = await attemptTier(1, () => postLocalTransportInvoke(command, args) as Promise<T>);
    if (tier1 !== TRANSPORT_TIER_EXHAUSTED) return tier1;
  }

  if (_uriSchemeTransportSupported !== false) {
    const tier2 = await attemptTier(2, () => postUriSchemeTransportInvoke(command, args) as Promise<T>);
    if (tier2 !== TRANSPORT_TIER_EXHAUSTED) return tier2;
  }

  if (_localTransportBridgeSupported !== false) {
    const tier3 = await attemptTier(3, () => postLocalTransportBridgeInvoke(command, args) as Promise<T>);
    if (tier3 !== TRANSPORT_TIER_EXHAUSTED) return tier3;
  }

  const tier4 = await attemptTier(4, async () => {
    try {
      const result = await invokeTauriWithRetries<T>(command, args, timeoutMs);
      logTransportPathUsed(4, resolved);
      return result;
    } catch (err) {
      if (isTransportInvokePathFailure(err)) throw err;
      throw new TransportCommandError(4, resolved, invokeErrorMessage(err));
    }
  });
  if (tier4 !== TRANSPORT_TIER_EXHAUSTED) return tier4;

  if (options?.localOnly) {
    throw new LocalTiersExhaustedError(resolved);
  }

  try {
    return await postCloudRelayTransport<T>(command, args, cfg);
  } catch (relayErr) {
    if (relayErr instanceof TransportCommandError) throw relayErr;
    throw new Error(`transport path exhausted for ${resolved}: ${invokeErrorMessage(relayErr)}`);
  }
}

async function invokeTransportBackground<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (!isConnectorAvailable()) throw new Error('Maestro not available');
  await waitForIpcTransportReady();
  if (_ipcForcePostMessage || _ipcPostMessageFallback) {
    applyIpcFetchPatchIfNeeded(true);
  }
  if (!isTransportCommand(command, args)) {
    throw new Error(`invokeTransportBackground: not a transport command: ${command}`);
  }
  const timeoutMs = (_ipcPostMessageFallback || _ipcForcePostMessage)
    ? IPC_DEGRADED_INVOKE_TIMEOUT_MS
    : 0;
  return runTransportLadder<T>(command, args, timeoutMs, { localOnly: true });
}

function rawTauriInvoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  return (window as any).__TAURI__.core.invoke(command, args) as Promise<T>;
}

function invokeIpcWithTimeout<T>(
  command: string,
  args: Record<string, unknown> | undefined,
  timeoutMs: number,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  const raw = rawTauriInvoke<T>(command, args);
  // #10426-a1 double-play guard: exclusive_play that resolves after the web gave up must not keep playing.
  raw.then(() => {
    if (timedOut && command === 'exclusive_play') {
      void import('../transport/exclusiveTimeoutFallback').then((m) => m.onLateExclusivePlayIpcResolve()).catch(() => {});
    }
  }, () => {});
  return Promise.race([
    raw,
    new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => { timedOut = true; reject(new Error(`ipc invoke timeout: ${command} after ${timeoutMs}ms`)); },
        timeoutMs,
      );
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/** Shared Tauri invoke with IPC retry/ready handling — use for all Connector IPC including local library. */
export async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isConnectorAvailable()) throw new Error('Maestro not available');
  await waitForIpcTransportReady();
  if (_ipcForcePostMessage || _ipcPostMessageFallback) {
    applyIpcFetchPatchIfNeeded(true);
  }
  const transportCommand = isTransportCommand(command, args);
  const degraded = _ipcPostMessageFallback || _ipcForcePostMessage;
  const timeoutMs = degraded ? IPC_DEGRADED_INVOKE_TIMEOUT_MS : 0;

  if (transportCommand) {
    return runTransportLadder<T>(command, args, timeoutMs);
  }

  // WebKit blocks ipc:// as "insecure content" on HTTPS pages until Tauri
  // falls back to postMessage. Retry so early calls survive the switchover.
  return invokeTauriWithRetries<T>(command, args, nonTransportInvokeTimeoutMs(command, degraded));
}

/** Auth-critical invokes: transport-ready gate + optional hard timeout. */
async function invokeAuthCritical<T>(
  command: string,
  args?: Record<string, unknown>,
  timeoutMs?: number,
): Promise<T> {
  await waitForIpcTransportReady();
  const op = invoke<T>(command, args);
  if (!timeoutMs) return op;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      op,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${command} timeout after ${timeoutMs}ms`)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function connectorInvoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (_ipcPostMessageFallback && command.startsWith('plugin:')) {
    diagnosticLog('info', 'protocol', 'ipc_plugin_invoke_under_postMessage_fallback', { command });
  }
  return invoke<T>(command, args);
}

/** Normalize Tauri event data — `listen()` already unwraps `e.payload`, but some call sites double-unwrap. */
export function listenEventPayload(raw: unknown): Record<string, unknown> {
  if (raw != null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (obj.payload != null && typeof obj.payload === 'object' && !Array.isArray(obj.payload)) {
      return obj.payload as Record<string, unknown>;
    }
    return obj;
  }
  return {};
}

export async function listen(event: string, callback: (payload: any) => void): Promise<() => void> {
  if (!isConnectorAvailable()) return () => {};
  await waitForIpcTransportReady();
  // WebKit blocks ipc:// as "insecure content" from HTTPS pages. Tauri falls
  // back to postMessage, but in-flight listen() calls fail before the fallback
  // activates. Retry so the subscription is established after the switchover.
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      const unlisten = await (window as any).__TAURI__.event.listen(event, (e: any) => callback(e.payload));
      if (attempt > 0) console.log(`[connector] listen('${event}') succeeded on attempt ${attempt + 1}`);
      return unlisten;
    } catch (err) {
      if (attempt < 5) {
        markIpcPostMessageFallback(String(err));
        await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
      } else {
        console.error(`[connector] listen('${event}') failed after 6 attempts:`, err);
        throw err;
      }
    }
  }
  return () => {};
}

// Discovery
export async function getRenderers(): Promise<Renderer[]> {
  return invoke<Renderer[]>('get_renderers');
}

export async function probeDeviceIp(ip: string): Promise<string> {
  console.log('[connector.probeDeviceIp] probing:', ip);
  return invoke<string>('probe_device_ip', { ip });
}

export async function onRenderersUpdated(cb: (renderers: Renderer[]) => void): Promise<() => void> {
  return listen('renderers-updated', cb);
}

// Transport
export async function play(rendererId: string) {
  return invoke('play', { rendererId });
}

export async function pause(rendererId: string) {
  return invoke('pause', { rendererId });
}

export async function stop(rendererId: string) {
  return invoke('stop', { rendererId });
}

export type { ReattachPlaybackSession } from './connectorReattachTypes';

/** Reborn webview reads in-progress engine session — never resets transport. */
export async function reattachPlaybackSession(): Promise<import('./connectorReattachTypes').ReattachPlaybackSession | null> {
  if (!isConnectorAvailable()) return null;
  try {
    return await invoke<import('./connectorReattachTypes').ReattachPlaybackSession | null>('reattach_playback_session');
  } catch {
    return null;
  }
}

/** Playback-active heartbeat for idle sleep / App Nap prevention (Connector desktop). */
export async function setPlaybackActive(active: boolean): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke('set_playback_active', { active });
  } catch (e) {
    console.warn('[PowerManager] set_playback_active failed (non-fatal):', e);
  }
}

/** @deprecated Prefer setPlaybackActive with heartbeat while playing. */
export async function holdAwake(): Promise<void> {
  return setPlaybackActive(true);
}

/** Release playback power assertion. Failures are logged only. */
export async function releaseAwake(): Promise<void> {
  return setPlaybackActive(false);
}

/** Hold transport monitor + power assertion while Ella Radio stage-2 catches up. */
export async function setRadioSessionWaiting(active: boolean): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke('set_radio_session_waiting_cmd', { active });
  } catch (e) {
    console.warn('[RadioSession] set_radio_session_waiting failed (non-fatal):', e);
  }
}

export async function seek(rendererId: string, seconds: number) {
  return invoke('seek', { rendererId, seconds: Math.floor(seconds) });
}

export async function getVolume(rendererId: string): Promise<number> {
  return invoke('get_volume', { rendererId }) as Promise<number>;
}

/** Background volume hydration — local tiers only, never relay (#445). */
export async function getVolumeBackground(rendererId: string): Promise<number> {
  return invokeTransportBackground<number>('get_volume', { rendererId });
}

export async function setVolume(rendererId: string, volume: number) {
  const vol = Math.round(Math.min(100, Math.max(0, volume)));
  console.log('[connector.setVolume] invoke set_volume:', rendererId, vol);
  return invoke('set_volume', { rendererId, volume: vol });
}

export async function setUri(rendererId: string, uri: string, metadata = '') {
  return invoke('set_uri', { rendererId, uri, metadata });
}

export async function setNextUri(
  rendererId: string,
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
  localFileMeta?: { sampleRate?: number | null; bitDepth?: number | null; channelCount?: number | null; fileSize?: number | null },
) {
  const m = localFileMeta;
  return invoke('set_next_uri', { rendererId, uri, title, artist, album, formatId, durationSecs, artUrl: artUrl ?? null, sampleRate: m?.sampleRate ?? null, bitDepth: m?.bitDepth ?? null, channelCount: m?.channelCount ?? null, fileSize: m?.fileSize ?? null });
}

export async function playUri(
  rendererId: string,
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
  startSeconds?: number,
) {
  const payload = {
    uri,
    format_id: formatId,
    renderer_id: rendererId,
    title,
    artist,
    album,
    art_url: artUrl ?? null,
    duration: durationSecs,
    start_seconds: startSeconds != null && startSeconds > 1 ? Math.floor(startSeconds) : null,
    // Keep camelCase aliases for Tauri argument compatibility.
    rendererId,
    formatId,
    durationSecs,
    artUrl: artUrl ?? null,
    startSeconds: startSeconds != null && startSeconds > 1 ? Math.floor(startSeconds) : null,
  };
  await waitForIpcTransportReady();
  console.log('[Bridge] invoke payload:', payload);
  const { markPlayUriDispatchStarted } = await import('../transport/playUriConfirmation');
  markPlayUriDispatchStarted();
  return invoke('play_uri', payload);
}

export async function getTransportInfo(rendererId: string): Promise<string> {
  return invoke<string>('get_transport_info', { rendererId });
}

/** Background reconcile — local tiers only, never relay (#445). */
export async function getTransportInfoBackground(rendererId: string): Promise<string> {
  return invokeTransportBackground<string>('get_transport_info', { rendererId });
}

export interface MultizonePlayResult {
  renderer_id: string;
  success: boolean;
  error: string | null;
}

export async function multizonePlay(
  renderers: { renderer_id: string; volume: number | null }[],
  uri: string,
  title: string,
  artist: string,
  album: string,
  formatId: number,
  durationSecs: number,
  artUrl?: string,
): Promise<MultizonePlayResult[]> {
  console.log('[Bridge] multizonePlay:', renderers.length, 'zones');
  return invoke<MultizonePlayResult[]>('multizone_play', {
    renderers,
    uri,
    title,
    artist,
    album,
    formatId,
    format_id: formatId,
    durationSecs,
    duration_secs: durationSecs,
    artUrl: artUrl ?? null,
    art_url: artUrl ?? null,
  });
}

export async function multizonePause(): Promise<void> {
  console.log('[Bridge] multizonePause — cancelling drift monitor');
  return invoke<void>('multizone_pause');
}

export async function multizoneStop(): Promise<void> {
  console.log('[Bridge] multizoneStop — cancelling drift monitor + stopping all');
  return invoke<void>('multizone_stop');
}

// Set a user-chosen custom name for a network player. Persists across restarts.
// Pass empty string to clear the custom name and revert to the device's own name.
export async function renameRenderer(rendererId: string, customName: string): Promise<void> {
  return invoke<void>('rename_renderer', { rendererId, customName });
}

/** Per-renderer delay before Play when the next track's format/sample rate differs. */
export async function setRendererResyncDelay(rendererId: string, delayMs: number): Promise<void> {
  return invoke<void>('set_renderer_resync_delay', { rendererId, delayMs });
}

/** Per-renderer gapless SetNext toggle. When disabled, Maestro drives hard transitions. */
export async function setRendererGaplessEnabled(rendererId: string, enabled: boolean): Promise<void> {
  return invoke<void>('set_renderer_gapless_enabled', { rendererId, enabled });
}

export interface RendererPositionEvent {
  rendererId: string;
  relTimeSecs: number;
  trackDurationSecs?: number;
  trackNumber?: number;
  trackUri?: string | null;
  trackTitle?: string | null;
  trackArtist?: string | null;
  transportState?: string;
}

// Volume state feedback from device (physical knob, network renderer events)
export async function onVolumeChanged(cb: (data: { rendererId: string; volume: number }) => void): Promise<() => void> {
  return listen('volume-changed', cb);
}

// Transport state feedback — emitted when the device's real playback state changes
// (e.g. PLAYING, STOPPED, PAUSED_PLAYBACK, NO_MEDIA_PRESENT, TRANSITIONING)
export async function onTransportStateChanged(
  cb: (data: { rendererId: string; state: string }) => void,
): Promise<() => void> {
  return listen('transport-state-changed', cb);
}

export interface RendererTrackEndedEvent {
  rendererId: string;
  source?: string;
  durationSecs?: number;
}

/** Authoritative track-end from Connector timer monitor (Ideon/AirLens/Grimm MU2-class). */
export async function onRendererTrackEnded(
  cb: (data: RendererTrackEndedEvent) => void,
): Promise<() => void> {
  return listen('renderer-track-ended', (raw) => {
    const p = listenEventPayload(raw);
    cb({
      rendererId: String(p.rendererId ?? ''),
      source: p.source != null ? String(p.source) : undefined,
      durationSecs: p.durationSecs != null ? Number(p.durationSecs) : undefined,
    });
  });
}

export interface ExclusivePlaybackStartFailedEvent {
  rendererId: string;
  reason?: string;
  suppressWallClockAdvance?: boolean;
}

/** Connector #453 — exclusive open failed; web must not wall-clock-advance the queue. */
export async function onExclusivePlaybackStartFailed(
  cb: (data: ExclusivePlaybackStartFailedEvent) => void,
): Promise<() => void> {
  return listen('exclusive-playback-start-failed', (raw) => {
    const p = listenEventPayload(raw);
    cb({
      rendererId: String(p.rendererId ?? ''),
      reason: p.reason != null ? String(p.reason) : undefined,
      suppressWallClockAdvance: p.suppressWallClockAdvance === true
        || p.suppressWallClockAdvance === 'true',
    });
  });
}

export interface ExclusivePlaybackStartedEvent {
  rendererId: string;
  sampleRate?: number;
  bitDepth?: number;
  formatTransition?: boolean;
}

/** Exclusive engine confirmed audible output — clears failed-open wall-clock suppress. */
export async function onExclusivePlaybackStarted(
  cb: (data: ExclusivePlaybackStartedEvent) => void,
): Promise<() => void> {
  return listen('exclusive-playback-started', (raw) => {
    const p = listenEventPayload(raw);
    cb({
      rendererId: String(p.rendererId ?? ''),
      sampleRate: p.sampleRate != null ? Number(p.sampleRate) : undefined,
      bitDepth: p.bitDepth != null ? Number(p.bitDepth) : undefined,
      formatTransition: p.formatTransition === true || p.formatTransition === 'true',
    });
  });
}

/** Position + track metadata from Connector GetPositionInfo polls (v11.0.63+). */
export async function onRendererPositionChanged(
  cb: (data: RendererPositionEvent) => void,
): Promise<() => void> {
  return listen('renderer-position-changed', (raw) => {
    const p = listenEventPayload(raw);
    cb({
      rendererId: String(p.rendererId ?? ''),
      relTimeSecs: Number(p.relTimeSecs ?? 0),
      trackDurationSecs: p.trackDurationSecs != null ? Number(p.trackDurationSecs) : undefined,
      trackNumber: p.trackNumber != null ? Number(p.trackNumber) : undefined,
      trackUri: p.trackUri != null ? String(p.trackUri) : null,
      trackTitle: p.trackTitle != null ? String(p.trackTitle) : null,
      trackArtist: p.trackArtist != null ? String(p.trackArtist) : null,
      transportState: p.transportState != null ? String(p.transportState) : undefined,
    });
  });
}

// ---------------------------------------------------------------------------
// Local Library — Scanner + Folder Browser + Local File Playback
// iOS PARITY: These IPC calls only work inside the Connector (Tauri).
// iOS reads library data from Supabase after the Connector scans.
// ---------------------------------------------------------------------------

export interface ScannedTrack {
  file_path: string;
  title: string | null;
  artist: string | null;
  album: string | null;
  album_artist: string | null;
  year: number | null;
  genre: string | null;
  track_num: number | null;
  disc_num: number | null;
  duration: number | null;
  sample_rate: number | null;
  bit_depth: number | null;
  channels: number | null;
  format: string;
  file_size: number;
  has_cover_art: boolean;
  /** SHA256 of embedded ID3v2 / FLAC PICTURE / MP4 covr / DSF APIC bytes
   *  after the scanner saves them into the local cover_cache. The hash
   *  is the URL path segment for `http://127.0.0.1:{proxy_port}/cover/{hash}`. */
  embedded_cover_hash?: string | null;
  /** SHA256 of folder.jpg / cover.jpg / front.jpg sidecar bytes after
   *  ingest into cover_cache. */
  folder_cover_hash?: string | null;
}

export interface ScanProgress {
  files_found?: number;
  files_processed?: number;
  albums_identified?: number;
  /** Legacy string path; local-core may send a numeric file index instead. */
  current_file?: string | number;
  percent?: number;
  eta_seconds?: number;
  phase?: string;
  /** local-core walk progress (Connector v11.0.60+) */
  entries_seen?: number;
  audio_files?: number;
  elapsed_secs?: number;
  walk_phase?: string;
  current_path?: string;
  total_files?: number;
  /** v11.0.116 honest scan denominator */
  walk_files_found?: number;
  terminal_files?: number;
  pending_jobs?: number;
  skipped_count?: number;
  failed_count?: number;
  indexed_count?: number;
  set_aside?: {
    unreadable?: number;
    tag_error?: number;
    timeout?: number;
    permission?: number;
    other?: number;
  };
}

export interface ScanSkipRow {
  id: number;
  scan_run_id?: number | null;
  file_path: string;
  reason_code: string;
  reason_detail?: string | null;
  category: string;
  first_seen_at: string;
  last_seen_at: string;
}

export interface ScanSkipListResponse {
  skips: ScanSkipRow[];
  summary?: ScanProgress['set_aside'];
  total?: number;
  limit?: number;
  offset?: number;
  returned?: number;
  has_more?: boolean;
}

async function fetchScanSkipsFromProxy(limit: number, offset: number): Promise<ScanSkipListResponse> {
  const port = await ensureProxyPort();
  if (!port) throw new Error('Local catalog proxy is not running');
  const url = `http://127.0.0.1:${port}/api/catalog/scan-skips?limit=${limit}&offset=${offset}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error?.trim() || `Catalog request failed (${resp.status})`);
  }
  return resp.json() as Promise<ScanSkipListResponse>;
}

export interface ScanSummary {
  total_tracks: number;
  total_albums: number;
  duration_secs: number;
  files_unchanged?: number;
  files_new_or_changed?: number;
  incremental_enabled?: boolean;
  skipped_sources?: string[];
  walk_errors?: number;
}

export async function listDirectory(path: string): Promise<{ name: string; path: string; is_dir: boolean }[]> {
  try {
    return await invoke<{ name: string; path: string; is_dir: boolean }[]>('list_directory', { path });
  } catch {
    return [];
  }
}

/** Windows only — re-runs WNet + mDNS/subnet network share discovery. */
export async function refreshWindowsNetworkDiscovery(): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke<void>('refresh_windows_network_discovery');
  } catch (e) {
    console.warn('[connector] refresh_windows_network_discovery failed:', e);
  }
}

export async function checkSourcesAccessible(paths: string[]): Promise<Record<string, boolean>> {
  const result: Record<string, boolean> = {};
  for (const p of paths) {
    try {
      const entries = await listDirectory(p);
      result[p] = entries.length > 0;
    } catch {
      result[p] = false;
    }
  }
  return result;
}

// iOS PARITY: iOS cannot pick local folders — local library scanning
// is desktop-only. iOS accesses local files via BATON remote streaming.
// This function must never be called on iOS.
export async function pickMusicFolders(): Promise<string[]> {
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return [];
  const result = await invoke<string[]>('pick_music_folders');
  return result || [];
}

export async function scanLibrary(
  paths: string[],
  sources?: Array<{ id: string; path: string }>,
): Promise<ScanSummary> {
  if (sources && sources.length > 0) {
    return invoke<ScanSummary>('scan_library', { sources });
  }
  return invoke<ScanSummary>('scan_library', { paths });
}

/** Local-core scan — writes to on-device library DB; emits local-core:scan-progress. */
export async function scanLocalLibrarySources(
  sources: Array<{ id: string; path: string; scan_path?: string }>,
): Promise<ScanSummary> {
  const resp = await invoke<{
    success?: boolean;
    total_tracks?: number;
    total_albums?: number;
    files_new_or_changed?: number;
    files_unchanged?: number;
    skipped_sources?: string[];
    walk_errors?: number;
    error?: string;
  }>('local_library_scan_sources', { sources });
  if (resp?.success === false || resp?.error) {
    throw new Error(resp?.error || 'Local library scan failed');
  }
  return {
    total_tracks: resp?.total_tracks ?? 0,
    total_albums: resp?.total_albums ?? 0,
    duration_secs: 0,
    files_new_or_changed: resp?.files_new_or_changed,
    files_unchanged: resp?.files_unchanged,
    incremental_enabled: true,
    skipped_sources: resp?.skipped_sources,
    walk_errors: resp?.walk_errors,
  };
}

export async function onLocalScanProgress(
  cb: (event: Record<string, unknown>) => void,
): Promise<() => void> {
  return listen('local-core:scan-progress', (evt) => {
    const payload = (evt as { payload?: Record<string, unknown> })?.payload ?? evt;
    cb(payload as Record<string, unknown>);
  });
}

/** Unresolved scan skip ledger entries (v11.0.116+). Desktop Connector only. */
const SCAN_SKIPS_INVOKE_TIMEOUT_MS = 20_000;

function invokeWithTimeout<T>(
  invokeFn: () => Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    invokeFn().then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export async function listLocalLibraryScanSkips(
  limit = 500,
  offset = 0,
): Promise<ScanSkipListResponse> {
  const cappedLimit = Math.min(Math.max(limit, 1), 500);
  try {
    return await invokeWithTimeout(
      () => invoke<ScanSkipListResponse>('local_library_list_scan_skips', {
        limit: cappedLimit,
        offset,
      }),
      SCAN_SKIPS_INVOKE_TIMEOUT_MS,
      'Load set-aside list',
    );
  } catch (ipcErr) {
    const ipcRejected = isScanSkipsListCommandRejected(ipcErr);
    try {
      return await fetchScanSkipsFromProxy(cappedLimit, offset);
    } catch (httpErr) {
      if (ipcRejected || isScanSkipsListCommandRejected(httpErr)) {
        throw new ScanSkipsListUnsupportedError();
      }
      throw httpErr instanceof Error ? httpErr : new Error(invokeErrorMessage(httpErr) || 'Could not load the list');
    }
  }
}

/** Re-enqueue a skipped file for tag/artwork processing (v11.0.116+). */
export async function retryLocalLibraryScanSkip(skipId: number): Promise<{ retried: boolean; skip_id: number }> {
  return invoke<{ retried: boolean; skip_id: number }>('local_library_retry_scan_skip', { skipId });
}

export async function cancelScan(): Promise<void> {
  return invoke<void>('cancel_scan');
}

export async function onScanBatch(
  cb: (tracks: ScannedTrack[]) => void,
): Promise<() => void> {
  return listen('scan-batch', cb);
}

export async function playLocalFile(
  filePath: string,
  rendererId: string,
  title: string,
  artist: string,
  album: string,
  durationSecs: number,
  artworkId?: string | null,
  sampleRate?: number | null,
  bitDepth?: number | null,
  channelCount?: number | null,
  fileSize?: number | null,
): Promise<PlayLocalFileResult> {
  return invoke<PlayLocalFileResult>('play_local_file', {
    filePath,
    rendererId,
    title,
    artist,
    album,
    durationSecs,
    artworkId: artworkId ?? null,
    sampleRate: sampleRate ?? null,
    bitDepth: bitDepth ?? null,
    channelCount: channelCount ?? null,
    fileSize: fileSize ?? null,
  });
}

export async function onScanProgress(
  cb: (progress: ScanProgress) => void,
): Promise<() => void> {
  return listen('scan-progress', cb);
}

// ---------------------------------------------------------------------------
// Remote Access — Register a local file and return a proxy URL without playing
// Used when this Connector is serving files to a remote Maestro client.
// iOS PARITY: iOS would expose an equivalent via the native bridge.
// ---------------------------------------------------------------------------

export async function registerFileUrl(filePath: string): Promise<string> {
  return invoke<string>('register_file_url', { filePath });
}

/**
 * Set the allowed library source directories on the Connector.
 * Only files under these paths can be served via the proxy.
 */
export async function setLibraryPaths(paths: string[]): Promise<void> {
  return invoke<void>('set_library_paths', { paths });
}

// ---------------------------------------------------------------------------
// Cloudflare Tunnel — query the current tunnel URL from the Connector
// iOS PARITY: iOS fetches tunnel URL from Mycelium, not from Tauri IPC.
// ---------------------------------------------------------------------------

export async function getTunnelUrl(): Promise<string | null> {
  return invoke<string | null>('get_tunnel_url');
}

/** Live tunnel URL from the background daemon (Unix socket IPC). */
export async function queryDaemonTunnelUrl(): Promise<string | null> {
  try {
    const url = await invoke<string | null>('query_daemon_tunnel_url');
    return url || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Headless Service — install/uninstall auto-start on login
// ---------------------------------------------------------------------------

export async function startHeadlessService(): Promise<void> {
  return invoke<void>('start_headless_service');
}

export async function stopHeadlessService(): Promise<void> {
  return invoke<void>('stop_headless_service');
}

/** Return this machine's stable identifier (hostname-uuid). */
export async function getMachineId(): Promise<string> {
  return invoke<string>('get_machine_id');
}

/** Auto-enable remote access: install background service + ensure tunnel is running. */
export async function autoEnableRemoteAccess(): Promise<void> {
  return invoke<void>('auto_enable_remote_access');
}

/** Trigger macOS Local Network permission on the home library host. */
export async function warmLocalNetworkAccess(): Promise<void> {
  if (!isConnectorAvailable()) return;
  try {
    await invoke<void>('warm_local_network_access');
  } catch (e) {
    console.warn('[warmLocalNetworkAccess]', e);
  }
}

/** Poll for a live tunnel URL (daemon IPC or in-app cloudflared) and register with Mycelium. */
export async function registerLiveTunnelWithCloud(
  maxWaitMs = 45000,
): Promise<string | null> {
  if (!isConnectorAvailable()) return null;
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const url = (await queryDaemonTunnelUrl()) || (await getTunnelUrl());
    if (url) {
      try {
        const machineId = await getMachineId();
        const { libraryService } = await import('../services/libraryService');
        await libraryService.registerTunnel(url, machineId);
        try {
          const { useMaestroStore } = await import('../store/maestroStore');
          useMaestroStore.getState().setTunnelConnected(true);
        } catch { /* optional */ }
        return url;
      } catch (e) {
        console.warn('[registerLiveTunnelWithCloud] register failed:', e);
        return url;
      }
    }
    await new Promise((r) => setTimeout(r, 2000));
  }
  return null;
}

/** Full remote-access setup: auth for background service + daemon install + tunnel. */
export async function enableHomeLibraryRemoteAccess(options?: {
  accessToken?: string | null;
  refreshToken?: string | null;
  userId?: string | null;
  email?: string | null;
}): Promise<void> {
  const { resolveRefreshTokenForDaemon } = await import('../components/AuthView');
  const resolved = await resolveRefreshTokenForDaemon();

  const refreshToken = (options?.refreshToken && isValidRefreshToken(options.refreshToken))
    ? options.refreshToken
    : resolved.refreshToken;
  const userId = options?.userId ?? resolved.userId ?? '';
  const email = options?.email ?? resolved.email ?? '';
  const accessToken = options?.accessToken ?? resolved.accessToken ?? null;

  if (isValidRefreshToken(refreshToken)) {
    const { writeDaemonCredentials } = await import('./authCredentialSeed');
    await writeDaemonCredentials(refreshToken, userId, email, 'enableHomeLibraryRemoteAccess');
  } else {
    console.warn('[enableHomeLibraryRemoteAccess] No valid refresh token — installing service anyway; sign out and back in if tunnel fails to register');
  }
  if (accessToken) {
    await setAuthToken(accessToken);
  }
  await warmLocalNetworkAccess();
  await autoEnableRemoteAccess();

  // Verify the LaunchAgent actually installed — auto_enable can swallow Rust errors.
  if (isConnectorAvailable()) {
    await new Promise((r) => setTimeout(r, 2500));
    try {
      let verification = await verifyDaemonInstallation();
      let basic = await getDaemonStatus().catch(() => null);
      let installed =
        verification.plist_exists ||
        (basic?.installed === true);

      if (!installed || (!verification.daemon_running && basic && !basic.running)) {
        // Retry install + start once (fixes corrupt 0-byte binary from older builds).
        console.warn('[enableHomeLibraryRemoteAccess] daemon not healthy — reinstalling');
        try {
          await installDaemon();
        } catch (installErr) {
          console.warn('[enableHomeLibraryRemoteAccess] installDaemon failed:', installErr);
        }
        await startDaemon().catch(() => {});
        await autoEnableRemoteAccess().catch(() => {});
        await new Promise((r) => setTimeout(r, 3000));
        verification = await verifyDaemonInstallation();
        basic = await getDaemonStatus().catch(() => null);
        installed =
          verification.plist_exists ||
          (basic?.installed === true);
      }

      if (!installed) {
        throw new Error('Background service did not install. Try “Install Background Service” below, or restart Maestro.');
      }
      const daemonHealthy = verification.daemon_running || (basic?.running === true);
      if (!daemonHealthy) {
        const liveUrl = await registerLiveTunnelWithCloud(45000);
        if (liveUrl) {
          console.warn(
            '[enableHomeLibraryRemoteAccess] Background service not running — registered in-app tunnel (works while Maestro stays open). Check ~/Library/Logs/MaestroConnector.log if the service keeps failing.',
          );
          return;
        }
        throw new Error(
          'Background service installed but not running, and no tunnel could be started. Try “Reinstall Service” below, or check ~/Library/Logs/MaestroConnector.log',
        );
      }
    } catch (e) {
      if (e instanceof Error && (
        e.message.includes('Background service') ||
        e.message.includes('not running')
      )) throw e;
      // verifyDaemonInstallation unavailable on older builds — continue
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('maestro:remote-access-changed'));
  }
}

/** Shut down remote access: stop tunnel, uninstall login item. */
export async function shutdownRemoteAccess(): Promise<void> {
  return invoke<void>('shutdown_remote_access');
}

/** Check whether the headless service plist exists and is loaded. */
export async function checkHeadlessServiceStatus(): Promise<{
  plist_exists: boolean;
  service_loaded: boolean;
}> {
  return invoke<{ plist_exists: boolean; service_loaded: boolean }>(
    'check_headless_service_status',
  );
}

/** Persist the user's JWT so headless mode can register tunnels with Mycelium. */
export async function setAuthToken(token: string): Promise<void> {
  return invoke<void>('set_auth_token', { token });
}

/** Listen for tunnel URL changes (emitted by Rust when cloudflared establishes a new tunnel). */
export async function onTunnelUrlChanged(
  cb: (url: string) => void,
): Promise<() => void> {
  return listen('tunnel-url-changed', cb);
}

/** Emitted when a network renderer never fetched audio from Maestro's HTTP proxy (~10s after play). */
export interface ProxyFetchFailedPayload {
  renderer_name: string;
  renderer_ip: string;
  stream_host: string;
  stream_port: number;
  platform: string;
  /** Connector v11.0.69+: host self-probe via advertised LAN IP before emit. */
  self_probe?: 'ok' | 'fail';
  guidance?: string;
  failure_step?: string;
  /** Connector delivery evidence — a fetch already reached us. */
  device_reached_us?: boolean;
  stream_in_flight?: boolean;
  bytes_delivered?: number;
  last_delivery_age_ms?: number;
  fetch_from?: string;
}

export async function onProxyFetchFailed(
  cb: (payload: ProxyFetchFailedPayload) => void,
): Promise<() => void> {
  return listen('proxy_fetch_failed', cb);
}

/** Connector "Proxy stream request" — renderer started downloading from the proxy. */
export interface ProxyStreamDeliveryPayload {
  renderer_ip?: string;
  renderer_name?: string;
  renderer_id?: string;
}

export async function onProxyStreamRequest(
  cb: (payload: ProxyStreamDeliveryPayload) => void,
): Promise<() => void> {
  return listen('proxy_stream_request', cb);
}

export async function onProxyStreamComplete(
  cb: (payload: ProxyStreamDeliveryPayload) => void,
): Promise<() => void> {
  return listen('proxy_stream_complete', cb);
}

/** Connector v11.0.174+: single-owner refresh result from `ensure_fresh_token`. */
export interface EnsureFreshTokenResult {
  access_token: string;
  refresh_token: string;
  rotated: boolean;
}

let _ensureFreshTokenSupported: boolean | null = null;

export function isEnsureFreshTokenSupported(): boolean | null {
  return _ensureFreshTokenSupported;
}

/**
 * True only when the Connector binary lacks the command (pre-174).
 * Timeouts and other invoke failures must NOT match — `#602` m3167: any error
 * containing `ensure_fresh_token` (including `timeout after 10000ms`) was
 * latched as unsupported, then api.ts POSTed /api/auth/refresh while the
 * daemon still owned rotation → already_used → auth_family_revoked.
 */
export function isUnknownTauriCommandError(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  if (msg.includes('timeout')) return false;
  return msg.includes('unknown command')
    || /command ['"]?[\w:]+['"]? not found/.test(msg)
    || (msg.includes('not found') && (msg.includes('unknown') || msg.includes('acl')));
}

/** Invoke Connector-owned refresh (v11.0.174+). Throws on auth/network errors. */
export async function ensureFreshToken(): Promise<EnsureFreshTokenResult> {
  return invokeAuthCritical<EnsureFreshTokenResult>(
    'ensure_fresh_token',
    undefined,
    ENSURE_FRESH_TOKEN_TIMEOUT_MS,
  );
}

export type TryEnsureFreshTokenOutcome =
  | { kind: 'ok'; result: EnsureFreshTokenResult }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string };

/**
 * Probe `ensure_fresh_token` once; pre-174 Connectors return unsupported so api.ts
 * keeps the legacy direct-refresh path unchanged.
 */
export async function tryEnsureFreshToken(): Promise<TryEnsureFreshTokenOutcome> {
  if (!isConnectorAvailable()) return { kind: 'unsupported' };
  if (_ensureFreshTokenSupported === false) return { kind: 'unsupported' };
  const { shouldSuppressAutomaticRefresh } = await import('./authTerminalLatch');
  if (shouldSuppressAutomaticRefresh()) return { kind: 'error', message: 'auth_family_revoked' };
  try {
    const result = await ensureFreshToken();
    _ensureFreshTokenSupported = true;
    return { kind: 'ok', result };
  } catch (err) {
    if (isUnknownTauriCommandError(err)) {
      _ensureFreshTokenSupported = false;
      return { kind: 'unsupported' };
    }
    return { kind: 'error', message: String(err) };
  }
}

export interface SessionExpiredPayload {
  reason?: string;
  trigger?: string;
  remote_access_affected?: boolean;
  user_message?: string;
}

export type RemoteAccessRevokedPayload = SessionExpiredPayload;

/** Emitted when the auth refresh family is revoked (service → GUI IPC relay). */
export async function onSessionExpired(
  cb: (payload: SessionExpiredPayload) => void,
): Promise<() => void> {
  return listen('session_expired', (payload) => cb(listenEventPayload(payload) as SessionExpiredPayload));
}

/** v11.0.191+: enriched remote-access revoke event (same payload shape as session_expired). */
export async function onRemoteAccessRevoked(
  cb: (payload: RemoteAccessRevokedPayload) => void,
): Promise<() => void> {
  return listen('remote_access_revoked', (payload) => {
    cb(listenEventPayload(payload) as RemoteAccessRevokedPayload);
  });
}

export type AuthHealthReason = 'refresh_token_already_used' | 'family_revoked';

export type AuthHealthPayload =
  | { status: 'healthy' }
  | { status: 'degraded' }
  | {
      status: 'reauth_required';
      reason?: AuthHealthReason;
      since_unix?: number;
    };

export type TryGetAuthHealthOutcome =
  | { kind: 'ok'; health: AuthHealthPayload }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string };

let _authHealthSupported: boolean | null = null;

/** Emitted when connector auth family health changes (PR #24+). */
export async function onAuthHealth(
  cb: (payload: AuthHealthPayload) => void,
): Promise<() => void> {
  return listen('auth:health', (payload) => {
    cb(listenEventPayload(payload) as AuthHealthPayload);
  });
}

/**
 * Boot-time auth health probe; pre-PR#24 Connectors return unsupported so the UI
 * behaves exactly as today (no panel, no errors).
 */
export async function tryGetAuthHealth(): Promise<TryGetAuthHealthOutcome> {
  if (!isConnectorAvailable()) return { kind: 'unsupported' };
  if (_authHealthSupported === false) return { kind: 'unsupported' };
  try {
    const health = await invokeAuthCritical<AuthHealthPayload>('get_auth_health');
    _authHealthSupported = true;
    return { kind: 'ok', health };
  } catch (err) {
    if (isUnknownTauriCommandError(err)) {
      _authHealthSupported = false;
      return { kind: 'unsupported' };
    }
    return { kind: 'error', message: String(err) };
  }
}

/** Reset auth-health feature detect (tests only). */
export function resetAuthHealthSupportForTests(): void {
  _authHealthSupported = null;
}

/** Reset ensure_fresh_token feature detect (tests only). */
export function resetEnsureFreshTokenSupportForTests(): void {
  _ensureFreshTokenSupported = null;
}

/** Emitted when the embedded local library database fails to open at startup. */
export interface LocalCoreInitFailedPayload {
  error: string;
  db_path: string;
  recoverable: boolean;
}

export async function onLocalCoreInitFailed(
  cb: (payload: LocalCoreInitFailedPayload) => void,
): Promise<() => void> {
  return listen('local_core_init_failed', cb);
}

export async function onLocalCoreResetComplete(
  cb: (payload: { db_path: string }) => void,
): Promise<() => void> {
  return listen('local_core_reset_complete', cb);
}

export async function onLocalLibraryOrphanCatalog(
  cb: (payload: { report?: { orphanAlbums?: number; orphanTracks?: number; orphanFiles?: number; configuredSources?: number; samplePaths?: string[] } }) => void,
): Promise<() => void> {
  return listen('local_library_orphan_catalog', cb);
}

export async function cleanupOrphanCatalog(): Promise<{ success: boolean; result?: Record<string, unknown> }> {
  return invoke('local_library_cleanup_orphan_catalog');
}

export async function repairLibrarySources(): Promise<{ success: boolean; report?: Record<string, unknown> }> {
  return invoke('local_library_repair_sources');
}

/** Delete the local library database and re-initialize (requires user confirmation in UI). */
export async function resetLocalDb(): Promise<void> {
  return invoke<void>('reset_local_db');
}

// ---------------------------------------------------------------------------
// Daemon management — install, uninstall, start, stop, verify
// ---------------------------------------------------------------------------

export interface DaemonVerification {
  plist_exists: boolean;
  daemon_running: boolean;
  tunnel_url_registered: boolean;
  update_available: boolean;
  update_version: string | null;
  error: string | null;
}

/** Get basic daemon status (installed, running, pid). Works on all Connector versions. */
export async function getDaemonStatus(): Promise<{
  installed: boolean;
  running: boolean;
  pid: number | null;
  tunnel_url: string | null;
}> {
  return invoke('get_daemon_status');
}

/** Comprehensive daemon installation verification for Settings UI. */
export async function verifyDaemonInstallation(): Promise<DaemonVerification> {
  return invoke<DaemonVerification>('verify_daemon_installation');
}

/** Install the background service daemon (macOS LaunchAgent / Windows Service). */
export async function installDaemon(): Promise<void> {
  return invoke<void>('install_daemon');
}

/** Uninstall the background service daemon. */
export async function uninstallDaemon(): Promise<void> {
  return invoke<void>('uninstall_daemon');
}

/** Full uninstall — daemon + all Maestro data from disk. */
export async function fullUninstall(): Promise<string> {
  return invoke<string>('full_uninstall');
}

/** Start the daemon via launchctl/sc. */
export async function startDaemon(): Promise<void> {
  return invoke<void>('start_daemon');
}

/** Stop the daemon via launchctl/sc. */
export async function stopDaemon(): Promise<void> {
  return invoke<void>('stop_daemon');
}

/** Read persisted auth credentials from auth.json (session restore on cold start). */
export async function readAuthJson(): Promise<{
  refresh_token: string;
  user_id: string;
  email: string;
} | null> {
  try {
    return await invokeAuthCritical<{ refresh_token: string; user_id: string; email: string }>(
      'read_auth_json',
      undefined,
      ENSURE_FRESH_TOKEN_TIMEOUT_MS,
    );
  } catch {
    return null;
  }
}

export interface WriteAuthJsonResult {
  written: boolean;
}

function isDaemonOwnsAuthWriteDenial(err: unknown): boolean {
  const msg = String(err).toLowerCase();
  return msg.includes('daemon owns auth')
    || msg.includes('sole writer')
    || msg.includes('service_token_owner');
}

/** Begin explicit sign-in flow — single-use nonce for seed_credentials (v11.0.211+). */
export async function beginAuthSeedNonce(): Promise<{ nonce: string }> {
  const result = await invokeAuthCritical<{ nonce?: string }>(
    'begin_auth_seed_nonce',
    undefined,
    ENSURE_FRESH_TOKEN_TIMEOUT_MS,
  );
  const nonce = result?.nonce;
  if (!nonce) throw new Error('begin_auth_seed_nonce returned no nonce');
  return { nonce };
}

/** Seed auth.json from sign-in UI — bypasses daemon-owns gate (v11.0.211+). */
export async function seedCredentials(
  nonce: string,
  refreshToken: string,
  userId: string,
  email: string,
  source = 'seedCredentials',
): Promise<WriteAuthJsonResult> {
  const len = (refreshToken || '').length;
  if (!isValidRefreshToken(refreshToken)) {
    const msg = `[seedCredentials] REJECTED — invalid refresh_token (len=${len})`;
    console.error(msg);
    throw new Error(msg);
  }
  const result = await invokeAuthCritical<{ written?: boolean } | void>(
    'seed_credentials',
    { nonce, refreshToken, userId, email },
    ENSURE_FRESH_TOKEN_TIMEOUT_MS,
  );
  if (result != null && typeof result === 'object' && result.written === false) {
    console.info(`[Auth:lifecycle] ${source} seed_credentials denied (written:false)`);
    return { written: false };
  }
  return { written: true };
}

/** Write auth credentials for the daemon. Validates token length. */
export async function writeAuthJson(
  refreshToken: string,
  userId: string,
  email: string,
  source = 'writeAuthJson',
): Promise<WriteAuthJsonResult> {
  const len = (refreshToken || '').length;
  console.log(`[writeAuthJson] refresh_token length=${len}, userId=${userId}`);
  if (!isValidRefreshToken(refreshToken)) {
    const msg = `[writeAuthJson] REJECTED — invalid refresh_token (len=${len})`;
    console.error(msg);
    throw new Error(msg);
  }
  try {
    const result = await invokeAuthCritical<{ written?: boolean } | void>('write_auth_json', {
      refreshToken,
      userId,
      email,
    }, ENSURE_FRESH_TOKEN_TIMEOUT_MS);
    if (result != null && typeof result === 'object' && result.written === false) {
      console.info(`[Auth:lifecycle] ${source} auth.json write denied (written:false)`);
      return { written: false };
    }
    return { written: true };
  } catch (err) {
    if (isDaemonOwnsAuthWriteDenial(err)) {
      console.info(`[Auth:lifecycle] ${source} auth.json write denied (daemon owns auth): ${err}`);
      return { written: false };
    }
    throw err;
  }
}

/** Get Connector version, platform, and build timestamp. */
export async function getConnectorInfo(): Promise<{
  version: string;
  platform: string;
  build_timestamp?: string;
  capabilities?: Record<string, boolean>;
}> {
  return invoke('get_connector_info');
}

/** Tail of Connector diagnostic + raw logs (same Rust sources as Help → Export Logs). */
export async function readSupportLogSnippet(maxBytes = 120_000): Promise<string> {
  if (!isConnectorAvailable()) {
    throw new Error('Maestro Connector not detected (__TAURI__ missing)');
  }
  return invoke<string>('read_support_log_snippet', { max_bytes: maxBytes });
}

/** Human-readable log location when zip export is unavailable (macOS default). */
export const CONNECTOR_SUPPORT_LOG_PATH_HINT = '~/Library/Logs/MaestroConnector.log';

export const EXPORT_LOGS_IPC_TIMEOUT_MS = 120_000;
/** Bounded fallback when custom-protocol IPC is wedged (#516 / #224 escape hatch). */
export const EXPORT_LOGS_FALLBACK_TIMEOUT_MS = 20_000;
const EXPORT_LOGS_FALLBACK_ATTEMPTS = 3;

/**
 * Desktop Help/Settings → Export Logs.
 * Never waits on waitForIpcTransportReady() — that gate is the last #224 hole
 * during wedges/rebuilds. Invokes export_logs_zip directly; on IPC block,
 * applies postMessage fallback and retries with a shorter bound.
 */
export async function exportLogsZip(): Promise<string> {
  if (!isConnectorAvailable()) {
    throw new Error('Maestro Connector not detected (__TAURI__ missing)');
  }
  if (_ipcForcePostMessage || _ipcPostMessageFallback) {
    applyIpcFetchPatchIfNeeded(true);
  }
  console.info('[connector] export_logs_zip escape-hatch invoke (no transport-ready gate)');

  let lastErr: unknown;
  for (let attempt = 0; attempt < EXPORT_LOGS_FALLBACK_ATTEMPTS; attempt++) {
    const timeoutMs = (_ipcPostMessageFallback || _ipcForcePostMessage)
      ? EXPORT_LOGS_FALLBACK_TIMEOUT_MS
      : EXPORT_LOGS_IPC_TIMEOUT_MS;
    try {
      const path = await invokeIpcWithTimeout<string>('export_logs_zip', undefined, timeoutMs);
      if (!path?.trim()) {
        throw new Error('Export returned no file path');
      }
      return path;
    } catch (err) {
      lastErr = err;
      if (isTauriAclDenied(err)) throw err;
      const retryable = isIpcTransportBlockError(err) || isIpcInvokeHangError(err);
      if (retryable && attempt < EXPORT_LOGS_FALLBACK_ATTEMPTS - 1) {
        markIpcPostMessageFallback(String(err));
        applyIpcFetchPatchIfNeeded(true);
        await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr ?? 'export_logs_zip failed'));
}

let connectorInfoBootstrap: Promise<void> | null = null;

/** Bootstrap `window.__MAESTRO_CONNECTOR__` from Rust (authoritative platform). Idempotent. */
export async function ensureConnectorInfo(): Promise<void> {
  if (!isConnectorAvailable()) return;
  if (connectorInfoBootstrap) return connectorInfoBootstrap;
  connectorInfoBootstrap = (async () => {
    try {
      const info = await getConnectorInfo();
      const { normalizeDesktopOsToken } = await import('../utils/platform');
      const platform = normalizeDesktopOsToken(info.platform);
      if (typeof window !== 'undefined') {
        (window as any).__MAESTRO_CONNECTOR__ = {
          ...((window as any).__MAESTRO_CONNECTOR__ || {}),
          ...info,
          platform: platform ?? info.platform,
        };
        if (platform) {
          (window as any).__MAESTRO_DESKTOP_OS__ = platform;
        }
      }
    } catch (err) {
      console.warn('[connector] ensureConnectorInfo failed:', err);
    }
  })();
  return connectorInfoBootstrap;
}

// ---------------------------------------------------------------------------
// Local cover-art proxy port
// ---------------------------------------------------------------------------
//
// The Connector's HTTP proxy serves embedded/folder cover art from a
// content-addressable cache at `GET /cover/{hash}`. The proxy port is
// chosen at startup so the frontend has to ask for it once, cache it on
// the global, and then build URLs synchronously from any component.
//
// Browser/iOS preview (no Connector): all calls return null and the
// online cover URL takes over via the existing fallback chain.

const PROXY_PORT_GLOBAL = '__MAESTRO_PROXY_PORT';
const PROXY_HOST_IP_GLOBAL = '__MAESTRO_PROXY_HOST_IP';
let proxyPortBootstrap: Promise<number | null> | null = null;

export interface ProxyInfo {
  host_ip: string;
  port: number;
}

/** Fetch live { host_ip, port } from the Connector proxy (never hardcoded). */
export async function getProxyInfo(): Promise<ProxyInfo | null> {
  if (!isConnectorAvailable()) return null;
  try {
    const info = await invoke<ProxyInfo>('get_proxy_info');
    if (info?.port > 0 && info.host_ip) {
      if (typeof window !== 'undefined') {
        (window as any)[PROXY_PORT_GLOBAL] = info.port;
        (window as any)[PROXY_HOST_IP_GLOBAL] = info.host_ip;
      }
      return info;
    }
    return null;
  } catch (err) {
    console.warn('[connector] get_proxy_info failed:', err);
    return null;
  }
}

/** Fetch the local proxy port via IPC. Returns null when not running
 *  inside the Connector or when the proxy hasn't bound yet. */
export async function getProxyPort(): Promise<number | null> {
  const info = await getProxyInfo();
  if (info?.port) return info.port;
  if (!isConnectorAvailable()) return null;
  try {
    const port = await invoke<number>('get_proxy_port');
    return port > 0 ? port : null;
  } catch (err) {
    console.warn('[connector] get_proxy_port failed:', err);
    return null;
  }
}

/** Idempotent bootstrap: invoke `get_proxy_info` once at app init and
 *  cache the result on `window.__MAESTRO_PROXY_PORT`. Subsequent calls
 *  return the cached value without hitting IPC. Safe to call from
 *  multiple components — concurrent callers share the same in-flight
 *  promise. */
export async function ensureProxyPort(): Promise<number | null> {
  if (typeof window === 'undefined') return null;
  const cached = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof cached === 'number' && cached > 0) return cached;
  if (proxyPortBootstrap) return proxyPortBootstrap;
  proxyPortBootstrap = (async () => {
    const info = await getProxyInfo();
    const port = info?.port ?? (await getProxyPort());
    if (port && port > 0) {
      (window as any)[PROXY_PORT_GLOBAL] = port;
      if (info?.host_ip) {
        (window as any)[PROXY_HOST_IP_GLOBAL] = info.host_ip;
      }
    }
    return port;
  })();
  return proxyPortBootstrap;
}

/** True when `http://127.0.0.1:{port}/cover/…` img/fetch URLs are blocked
 *  (HTTPS Pages shell loading inside Connector WebView). Use Tauri IPC instead. */
export function isHttpsCoverContext(): boolean {
  if (typeof window === 'undefined') return false;
  const protocol = window.location?.protocol;
  if (!protocol) return false;
  if (protocol !== 'https:') return false;
  const host = window.location?.hostname || '';
  return host !== 'localhost' && host !== '127.0.0.1';
}

/** Build a direct `http://127.0.0.1:{port}/cover/{hash}` URL. Works only on
 *  same-origin HTTP (legacy). On HTTPS sandbox/production, returns null — use
 *  coverArtIPC (`read_cover_art`) instead. Pass `{ thumb: true }` for grid tiles. */
export function buildLocalCoverUrl(
  hash?: string | null,
  options?: { thumb?: boolean },
): string | null {
  if (!hash || typeof hash !== 'string') return null;
  if (typeof window === 'undefined') return null;
  if (isHttpsCoverContext()) return null;
  const port = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof port !== 'number' || port <= 0) return null;
  const base = `http://127.0.0.1:${port}/cover/${hash}`;
  if (options?.thumb) {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}size=thumb`;
  }
  return base;
}

/** Build `http://127.0.0.1:{port}/thumb/{16-char-key}` — v11.0.215+ disk JPEG. */
export function buildLocalThumbUrl(thumbKey?: string | null): string | null {
  if (!thumbKey || typeof thumbKey !== 'string') return null;
  const key = thumbKey.trim().toLowerCase();
  if (!/^[0-9a-f]{16}$/.test(key)) return null;
  if (typeof window === 'undefined') return null;
  if (isHttpsCoverContext()) return null;
  const port = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof port !== 'number' || port <= 0) return null;
  return `http://127.0.0.1:${port}/thumb/${key}`;
}

const _thumbBlobCache = new Map<string, string>();
const _thumbFetchInFlight = new Map<string, Promise<string | null>>();

/** Fetch pre-generated disk thumb via GET /thumb/{key}; validates size/type. */
export async function fetchLocalThumbUrl(
  thumbKey?: string | null,
): Promise<string | null> {
  if (!thumbKey || typeof thumbKey !== 'string') return Promise.resolve(null);
  const key = thumbKey.trim().toLowerCase();
  if (!/^[0-9a-f]{16}$/.test(key)) return Promise.resolve(null);
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isHttpsCoverContext()) return Promise.resolve(null);

  const cached = _thumbBlobCache.get(key) || getBlobCoverUrl(`thumb:${key}`);
  if (cached) {
    _thumbBlobCache.set(key, cached);
    return Promise.resolve(cached);
  }

  const inflight = _thumbFetchInFlight.get(key);
  if (inflight) return inflight;

  const promise = (async (): Promise<string | null> => {
    try {
      const url = buildLocalThumbUrl(key);
      if (!url) return null;
      const resp = await loopbackFetch(url, { cache: 'no-store' });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      if (!validateThumbBlob(blob)) return null;
      const blobUrl = putBlobCoverUrl(`thumb:${key}`, blob);
      _thumbBlobCache.set(key, blobUrl);
      return blobUrl;
    } catch {
      return null;
    } finally {
      _thumbFetchInFlight.delete(key);
    }
  })();

  _thumbFetchInFlight.set(key, promise);
  return promise;
}

const _coverBlobCache = new Map<string, string>();
const _coverFetchInFlight = new Map<string, Promise<string | null>>();

function coverBlobCacheKey(hash: string, thumb?: boolean): string {
  return thumb ? `${hash}:thumb` : hash;
}

/** Fetch cover art bytes from the local proxy via fetch() and return a
 *  blob: URL. This bypasses mixed-content restrictions that can block
 *  `<img src="http://...">` on HTTPS pages in some contexts (e.g. the
 *  album grid). Results are cached — the same hash always returns the
 *  same blob URL. Returns null on error. */
export function fetchLocalCoverUrl(
  hash?: string | null,
  options?: { thumb?: boolean },
): Promise<string | null> {
  if (!hash || typeof hash !== 'string') return Promise.resolve(null);
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isHttpsCoverContext()) return Promise.resolve(null);
  const port = (window as any)[PROXY_PORT_GLOBAL];
  if (typeof port !== 'number' || port <= 0) return Promise.resolve(null);

  const cacheKey = coverBlobCacheKey(hash, options?.thumb);
  const cached = _coverBlobCache.get(cacheKey) || getBlobCoverUrl(cacheKey);
  if (cached) {
    _coverBlobCache.set(cacheKey, cached);
    return Promise.resolve(cached);
  }

  const inflight = _coverFetchInFlight.get(cacheKey);
  if (inflight) return inflight;

  const promise = (async (): Promise<string | null> => {
    try {
      const qs = options?.thumb ? '?size=thumb' : '';
      const resp = await fetch(`http://127.0.0.1:${port}/cover/${hash}${qs}`);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      const url = putBlobCoverUrl(cacheKey, blob);
      _coverBlobCache.set(cacheKey, url);
      return url;
    } catch {
      return null;
    } finally {
      _coverFetchInFlight.delete(cacheKey);
    }
  })();

  _coverFetchInFlight.set(cacheKey, promise);
  return promise;
}

// ---------------------------------------------------------------------------
// macOS Audio Output Device Selection
// ---------------------------------------------------------------------------

export interface AudioOutputDevice {
  id: string;
  name: string;
  is_default: boolean;
  /** "usb", "built_in", "bluetooth", "hdmi", "airplay", "virtual", "aggregate", "thunderbolt", "displayport", "pci", "firewire", "unknown" */
  transport_type: string;
  manufacturer: string;
  /** CoreAudio device UID — stable across reboots, contains USB product info */
  uid: string;
  /** Heuristic: true for standalone USB DACs, false for USB monitors/speakers/headsets */
  is_likely_dac: boolean;
}

export async function getAudioOutputDevices(): Promise<AudioOutputDevice[]> {
  return invoke<AudioOutputDevice[]>('get_audio_output_devices');
}

/** Why the default output is being set (#1349). Connectors before v11.0.328 ignore it.
 *  `user_pick`: the user chose this output, so the Mac default follows it.
 *  `auto`: Maestro picked it on its own; the Mac default is left alone. */
export type AudioOutputIntent = 'user_pick' | 'auto';

export async function setAudioOutputDevice(deviceId: string, intent: AudioOutputIntent): Promise<void> {
  return invoke<void>('set_audio_output_device', { deviceId, intent });
}

/** Query real hardware-supported formats from a USB DAC's physical streams. */
export async function getDeviceHardwareFormats(deviceId: string): Promise<{
  formats: Array<{
    sample_rate: number;
    bit_depth: number;
    channels: number;
    /** "pcm" | "dsd" | "dsd_native" */
    format_type?: string;
    /** DSD rate label: "DSD64", "DSD128", "DSD256", "DSD512" */
    dsd_rate?: string | null;
  }>;
  max_sample_rate: number;
  max_bit_depth: number;
  source?: string;
}> {
  return invoke('get_device_hardware_formats', { deviceId });
}

/** Take or release CoreAudio hog mode on a USB DAC. Returns the new state (true = hogged). */
export async function setExclusiveMode(deviceId: string, enable: boolean): Promise<boolean> {
  return invoke<boolean>('set_exclusive_mode', { deviceId, enable });
}

/** Set device sample rate to match content, preventing CoreAudio resampling. Returns the rate actually set. */
export async function setDeviceSampleRate(deviceId: string, sampleRate: number): Promise<number> {
  return invoke<number>('set_device_sample_rate', { deviceId, sampleRate });
}

// ─── Exclusive (bit-perfect) audio engine ────────────────────────────────────

export interface ExclusivePlaybackState {
  is_playing: boolean;
  position_secs: number;
  duration_secs: number;
  sample_rate: number;
  bit_depth: number;
  channels: number;
  is_buffering: boolean;
  playback_started?: boolean;
  /** v11.0.166+: "dop" | "decimated_pcm" | "none" */
  dsd_output_mode?: string;
  /** v11.0.166+: source DSD rate in Hz; 0 when not DSD */
  source_dsd_rate_hz?: number;
  /** v11.0.172+: true when the exclusive engine has drained and the track ended */
  ended?: boolean;
  /** v11.0.172+: HAL/stream failure message; playback must not auto-advance or retry */
  stream_error?: string | null;
}

/** Start bit-perfect playback: fetches, decodes, writes PCM directly to USB DAC via HAL AudioUnit. */
export async function exclusivePlay(
  deviceName: string,
  url: string,
  sampleRate: number,
  bitDepth: number,
  isDsd = false,
): Promise<unknown> {
  const blocked = bitPerfectBlockedMessage();
  if (blocked) {
    throw new Error(`exclusive_mode_not_allowed: ${blocked}`);
  }
  // KILL SWITCH — remove when v164 ships (IPC choke point; cannot bypass _shouldUseExclusive)
  if (shouldBlockWindowsExclusivePlay()) {
    throw new Error(WINDOWS_EXCLUSIVE_KILL_SWITCH_ERROR);
  }
  // #10426-a1: Connector ≥ v11.0.328 resolves { output_mode, reason, message }; older builds resolve null.
  return invoke<unknown>('exclusive_play', { deviceName, url, sampleRate, bitDepth, isDsd });
}

export async function exclusivePause(): Promise<void> {
  return invoke<void>('exclusive_pause');
}

export async function exclusiveResume(): Promise<void> {
  return invoke<void>('exclusive_resume');
}

export async function exclusiveStop(): Promise<void> {
  return invoke<void>('exclusive_stop');
}

export async function exclusiveGetState(): Promise<ExclusivePlaybackState> {
  return invoke<ExclusivePlaybackState>('exclusive_get_state');
}

export interface UsbDriverStatus {
  has_driver: boolean;
  driver_name: string | null;
  needs_install: boolean;
  vendor_url: string | null;
  error?: string;
}

/** Check if a USB DAC has a working driver (Windows) or native support (macOS). */
export async function checkUsbDriverStatus(deviceName: string): Promise<UsbDriverStatus> {
  return invoke<UsbDriverStatus>('check_usb_driver_status', { deviceName });
}

// ---------------------------------------------------------------------------
// Manual update check (Settings → Check for Updates)
// ---------------------------------------------------------------------------
export async function triggerConnectorUpdateCheck(): Promise<string | void> {
  return invoke<string | void>('check_for_updates_manual');
}

export async function getLocalCoreEnabled(): Promise<boolean> {
  return invoke<boolean>('get_local_core_enabled');
}

export async function setLocalCoreEnabled(enabled: boolean): Promise<void> {
  return invoke<void>('set_local_core_enabled', { enabled });
}

export interface LocalCoreAdapterTrack {
  id: number;
  file_id: number;
  title: string;
  album_title?: string | null;
  artist_name?: string | null;
  file_path?: string | null;
  file_size?: number | null;
  duration_ms?: number | null;
  sample_rate?: number | null;
  bit_depth?: number | null;
  channels?: number | null;
  format?: string | null;
}

export interface LocalCoreAdapterAlbum {
  id: number;
  title: string;
  album_artist?: string | null;
  year?: number | null;
  track_count?: number | null;
}

export interface LocalCoreAdapterArtist {
  id: number;
  name: string;
  album_count?: number | null;
  track_count?: number | null;
}

export async function localLibraryGetAlbums(limit = 200, offset = 0): Promise<{
  enabled: boolean;
  albums: LocalCoreAdapterAlbum[];
}> {
  return invoke('local_library_get_albums', { limit, offset });
}

export async function localLibraryGetArtists(limit = 200, offset = 0): Promise<{
  enabled: boolean;
  artists: LocalCoreAdapterArtist[];
}> {
  return invoke('local_library_get_artists', { limit, offset });
}

export async function localLibraryGetTracks(limit = 500, offset = 0): Promise<{
  enabled: boolean;
  tracks: LocalCoreAdapterTrack[];
}> {
  return invoke('local_library_get_tracks', { limit, offset });
}

export async function localLibrarySearch(query: string, limit = 200): Promise<{
  enabled: boolean;
  tracks: LocalCoreAdapterTrack[];
  albums: LocalCoreAdapterAlbum[];
  artists: LocalCoreAdapterArtist[];
}> {
  return invoke('local_library_search', { query, limit });
}

// ---------------------------------------------------------------------------
// DSD / DoP capability commands
// ---------------------------------------------------------------------------

export interface DacCapabilityInfo {
  known: boolean;
  vendor: string | null;
  dop_support: boolean;
  max_dsd_rate: string | null;
  native_usb_dsd: boolean;
}

export async function getDacCapability(deviceName: string): Promise<DacCapabilityInfo> {
  return invoke<DacCapabilityInfo>('get_dac_capability', { deviceName });
}

export async function setForcePcmForDsd(enabled: boolean): Promise<void> {
  return invoke<void>('set_force_pcm_for_dsd', { enabled });
}

export async function getForcePcmForDsd(): Promise<boolean> {
  return invoke<boolean>('get_force_pcm_for_dsd');
}

export type { LocalLibraryDirectoryEntry, LocalLibraryDirectoryListing } from './connectorLocalLibraryDirectory';
export { listLocalLibraryDirectory } from './connectorLocalLibraryDirectory';
